'use client';
import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ArrowUp } from 'lucide-react';

function isPublicPath(pathname: string) {
  if (pathname === '/') return true;
  if (pathname === '/blog' || pathname.startsWith('/blog/')) return true;
  if (pathname === '/faqs') return true;
  if (pathname === '/contact') return true;
  if (pathname === '/privacy-policy') return true;
  if (pathname === '/terms-of-service') return true;
  if (pathname === '/data-processing-agreement') return true;
  return false;
}

export default function PublicScrollToTop() {
  const pathname = usePathname();
  const publicPath = isPublicPath(pathname);

  const popNavigationRef = useRef(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const onPopState = () => {
      popNavigationRef.current = true;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!publicPath) return;

    const onScroll = () => {
      setShowButton(window.scrollY > 300);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [publicPath]);

  useEffect(() => {
    if (!publicPath) return;

    if (popNavigationRef.current) {
      popNavigationRef.current = false;
      return;
    }

    if (window.location.hash) return;

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, publicPath]);

  if (!publicPath || !showButton) return null;

  return (
    <button
      type="button"
      aria-label="Back to top"
      onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-primary text-white shadow-lg border border-accent/30 hover:bg-primary/90 active:scale-[0.98] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <span className="sr-only">Back to top</span>
      <ArrowUp className="w-5 h-5 mx-auto" />
    </button>
  );
}
